import type { ProviderResult, ScoringMode } from './types'

/**
 * Mirrors the scoring configs from SearchEngineScoring.php.
 * Each rule has a key, label, and either a fixed weight or a field-based value.
 * The `applies` function evaluates whether the condition holds for a given provider.
 */

interface ConditionRule {
    type: 'condition'
    key: string
    label: string
    weight: number
    applies: (p: ProviderResult, cityUrl?: string) => boolean
}

interface ValueRule {
    type: 'value'
    key: string
    label: string
    getValue: (p: ProviderResult) => number
    format?: (v: number) => string
}

export type ScoringRule = ConditionRule | ValueRule

// --- Condition helpers ---
// isPremium mirrors SearchEngineScoring::queryHasPremiumFeatures() but omits per-service availability checks

function isPremium(p: ProviderResult): boolean {
    return (
        (p.premium_status === 'premium' || p.free_trial_status === 'free_trial_active') &&
        p.free_trial_status !== 'free_trial_limit_reached' &&
        !p.is_paused &&
        p.limits_status === 'available' &&
        p.active_status === 'active'
    )
}

function isActive(p: ProviderResult): boolean {
    return p.limits_status === 'available' && !p.is_paused
}

function isLocal(p: ProviderResult, cityUrl?: string): boolean {
    return !!cityUrl && p.city_url === cityUrl
}

// Mirrors SearchEngineScoring::createSoftDailyLimitReachedCondition(): per-account providers use
// the boolean flag, per-service providers match when the subscription the pro is matched with has
// reached its soft daily limit.
function softDailyLimitReached(p: ProviderResult): boolean {
    if (p.soft_daily_limit_reached === true) {
        return true
    }

    return (
        p.matched_subscription_id !== undefined &&
        (p.subscription_ids_soft_daily_limit_reached ?? []).includes(p.matched_subscription_id)
    )
}

// Mirrors SearchEngineScoring::createNoMatchesThisWeekCondition(). The counter only tracks leads
// received while premium, so a free trial or a basic pro sits on zero forever; isPremium() can't
// stand in for the premium check here because it also accepts an active free trial.
function noMatchesThisWeek(p: ProviderResult): boolean {
    return p.num_matches_this_week === 0 && p.premium_status === 'premium'
}

// --- Scoring configs per mode (mirrors SearchEngineScoring.php) ---
// Distance is already in km from the backend (ES script divides arcDistance by 1000)

const TOP10_RULES: ScoringRule[] = [
    {
        type: 'condition',
        key: 'doesRespond',
        label: 'Responds',
        weight: 20,
        applies: (p) => ['good', 'poor'].includes(p.response_label),
    },
    {
        type: 'condition',
        key: 'premiumLocal',
        label: 'Premium + Local',
        weight: 11,
        applies: (p, city) => isPremium(p) && isLocal(p, city),
    },
    {
        type: 'condition',
        key: 'premium',
        label: 'Premium',
        weight: 5,
        applies: (p) => isPremium(p),
    },
    {
        type: 'condition',
        key: 'isPausedWithPresence',
        label: 'Paused w/ presence',
        weight: 2,
        applies: (p) => !isActive(p) && !!p.has_active_presence_subscription,
    },
    { type: 'condition', key: 'active', label: 'Active', weight: 1, applies: (p) => isActive(p) },
    {
        type: 'value',
        key: 'totalScore',
        label: 'Trustoo score',
        getValue: (p) => p.total_score ?? 0,
    },
]

const RESULTS_RULES: ScoringRule[] = [
    {
        type: 'condition',
        key: 'doesRespond',
        label: 'Responds',
        weight: 200,
        applies: (p) => ['good', 'poor'].includes(p.response_label),
    },
    {
        type: 'condition',
        key: 'premiumLocal',
        label: 'Premium + Local',
        weight: 110,
        applies: (p, city) => isPremium(p) && isLocal(p, city),
    },
    {
        type: 'condition',
        key: 'premium',
        label: 'Premium',
        weight: 100,
        applies: (p) => isPremium(p),
    },
    { type: 'condition', key: 'active', label: 'Active', weight: 60, applies: (p) => isActive(p) },
    {
        type: 'condition',
        key: 'freemiumLocal',
        label: 'Freemium + Local',
        weight: 50,
        applies: (p, city) => !isPremium(p) && isLocal(p, city),
    },
    {
        type: 'value',
        key: 'totalScore',
        label: 'Trustoo score',
        getValue: (p) => p.total_score ?? 0,
    },
]

const MATCH_MORE_MULTIPLIER_RULES: ScoringRule[] = [
    {
        type: 'value',
        key: 'reclaimFactor',
        label: 'Reclaim factor',
        getValue: (p) => p.reclaim_factor ?? 1.0,
    },
    {
        type: 'condition',
        key: 'premium',
        label: 'Premium',
        weight: 100,
        applies: (p) => isPremium(p),
    },
    {
        type: 'value',
        key: 'leadBooster',
        label: 'Lead booster',
        getValue: (p) => (p.lead_booster_active ? (p.lead_booster_factor ?? 1.0) : 1.0),
    },
    {
        type: 'condition',
        key: 'within10km',
        label: '< 10 km',
        weight: 1.3,
        applies: (p) => p.distance != null && p.distance <= 10,
    },
    {
        type: 'condition',
        key: 'within25km',
        label: '10–25 km',
        weight: 1.1,
        applies: (p) => p.distance != null && p.distance > 10 && p.distance <= 25,
    },
    {
        type: 'condition',
        key: 'beyond75km',
        label: '> 75 km',
        weight: 0.85,
        applies: (p) => p.distance != null && p.distance > 75,
    },
    {
        type: 'condition',
        key: 'goodResponse',
        label: 'Good response',
        weight: 1.05,
        applies: (p) => p.response_label === 'good',
    },
    {
        type: 'condition',
        key: 'poorResponse',
        label: 'Poor response',
        weight: 0.5,
        applies: (p) => p.response_label === 'poor',
    },
    {
        type: 'condition',
        key: 'noResponse',
        label: 'No response',
        weight: 0.2,
        applies: (p) => p.response_label === 'no_response',
    },
    {
        type: 'condition',
        key: 'paymentPoor',
        label: 'Payment poor',
        weight: 0.25,
        applies: (p) => p.payment_score_label === 'poor',
    },
    {
        type: 'condition',
        key: 'softDailyLimitReached',
        label: 'Soft daily limit reached',
        weight: 0.7,
        applies: (p) => softDailyLimitReached(p),
    },
    {
        type: 'condition',
        key: 'noMatchesThisWeek',
        label: 'No leads this week',
        weight: 1.3,
        applies: (p) => noMatchesThisWeek(p),
    },
    {
        type: 'value',
        key: 'totalScore',
        label: 'Trustoo score',
        getValue: (p) => 1.0 + 0.01 * (p.total_score ?? 0),
        format: (v) => v.toFixed(4),
    },
]

export function getRulesForMode(mode: ScoringMode): {
    rules: ScoringRule[]
    isMultiplicative: boolean
} {
    switch (mode) {
        case 'landingpage':
            return { rules: TOP10_RULES, isMultiplicative: false }
        case 'results':
            return { rules: RESULTS_RULES, isMultiplicative: false }
        case 'wizard':
            return { rules: MATCH_MORE_MULTIPLIER_RULES, isMultiplicative: true }
        default:
            return { rules: MATCH_MORE_MULTIPLIER_RULES, isMultiplicative: true }
    }
}

export interface ScoringResult {
    rule: ScoringRule
    applies: boolean
    value: number
}

export function evaluateScoring(
    provider: ProviderResult,
    mode: ScoringMode,
    cityUrl?: string,
): { results: ScoringResult[]; total: number; isMultiplicative: boolean } {
    const { rules, isMultiplicative } = getRulesForMode(mode)
    const results: ScoringResult[] = []
    // Value that leaves the running total unchanged: multiply by 1 or add 0.
    const neutralValue = isMultiplicative ? 1 : 0
    let total = neutralValue

    for (const rule of rules) {
        let applies = false
        let value = 0

        if (rule.type === 'condition') {
            applies = rule.applies(provider, cityUrl)
            value = applies ? rule.weight : neutralValue
        } else {
            value = rule.getValue(provider)
            applies = value !== neutralValue
        }

        if (isMultiplicative) {
            total *= value
        } else {
            total += value
        }

        results.push({ rule, applies, value })
    }

    return { results, total, isMultiplicative }
}
